import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db, getEnvironmentTag } from '../firebase';
import { setLanguage } from '../i18n';
import PageMeta from '../components/PageMeta';
import { trackWaitlistSubmit, trackWaitlistError } from '../utils/analytics';
import heroRunnerImg from '../assets/hero_mountain_runner.webp';
import featureSyncImg from '../assets/feature_sync_watch_1767410341273.webp';
import featureBioImg from '../assets/feature_bio_focus_1767410355004.webp';
import featureRoutineImg from '../assets/feature_routine_prep_1767410369994.webp';
import marianaAvatar from '../assets/mariana_avatar.webp';
import athleteAvatar1 from '../assets/athlete_avatar_1.webp';
import athleteAvatar2 from '../assets/athlete_avatar_2.webp';
import athleteAvatar3 from '../assets/athlete_avatar_3.webp';

const WAITLIST_SOURCE = 'landing_page_v2_challenger';

const Features = () => {
    const { t } = useTranslation();

    const featuresData = [
        {
            title: t('landing.features.item1.title_v2'),
            description: t('landing.features.item1.desc_v2'),
            image: featureSyncImg,
            align: 'left'
        },
        {
            title: t('landing.features.item2.title_v2'),
            description: t('landing.features.item2.desc_v2'),
            image: featureBioImg,
            align: 'right'
        },
        {
            title: t('landing.features.item3.title_v2'),
            description: t('landing.features.item3.desc_v2'),
            image: featureRoutineImg,
            align: 'left'
        }
    ];

    return (
        <section id="features" className="py-24 relative scroll-mt-0 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-24">
                    <h2 className="text-base text-cyan-400 font-bold tracking-widest uppercase font-display mb-2">{t('landing.features.badge_v2')}</h2>
                    <p className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
                        {t('landing.features.mainTitle_v2')}
                    </p>
                    <p className="max-w-2xl mx-auto text-xl text-slate-400 font-sans font-light">
                        {t('landing.features.subtitle_v2')}
                    </p>
                </div>

                <div className="space-y-32">
                    {featuresData.map((feature, idx) => (
                        <div key={idx} className={`flex flex-col md:flex-row items-center gap-12 md:gap-24 ${feature.align === 'right' ? 'md:flex-row-reverse' : ''}`}>

                            {/* Text Block */}
                            <div className="flex-1 space-y-6">
                                <div className="w-12 h-1 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full"></div>
                                <h3 className="text-3xl md:text-4xl font-display font-bold text-white leading-tight">
                                    {feature.title}
                                </h3>
                                <p className="text-lg text-slate-400 font-sans leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>

                            {/* Image Block */}
                            <div className="flex-1 relative group perspective-container">
                                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-2xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 rotate-card transition-transform duration-700 ease-out group-hover:scale-[1.02]">
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent z-10"></div>
                                    <img
                                        src={feature.image}
                                        alt={feature.title}
                                        className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-110"
                                    />
                                </div>
                            </div>

                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ==========================================
   SUB-COMPONENTS (Inlined for Single-File Portability)
   ========================================== */

const Logo = ({ className = "" }) => {
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <svg width="36" height="32" viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <linearGradient id="logo-gradient" x1="0" y1="0" x2="36" y2="32" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#22d3ee" /> {/* cyan-400 */}
                        <stop offset="100%" stopColor="#34d399" /> {/* emerald-400 */}
                    </linearGradient>
                </defs>
                {/* Top Square (Position 1 - Most Intense) */}
                <rect x="12" y="0" width="12" height="12" rx="3" fill="url(#logo-gradient)" />
                {/* Bottom Left Square (Position 3 - Least Intense) */}
                <rect x="0" y="16" width="12" height="12" rx="3" fill="url(#logo-gradient)" fillOpacity="0.4" />
                {/* Bottom Right Square (Position 2 - Medium Intensity) */}
                <rect x="24" y="16" width="12" height="12" rx="3" fill="url(#logo-gradient)" fillOpacity="0.7" />
            </svg>
            <span className="font-display font-extrabold text-3xl tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">Podium</span>
        </div>
    );
};

const Navbar = () => {
    const { t, i18n } = useTranslation();

    return (
        <nav className="absolute top-0 w-full z-50 transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <Logo />
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            className={`bg-transparent border-none p-0 cursor-pointer transition-all duration-200 ${i18n.language?.startsWith('pt') ? 'opacity-100 scale-110 drop-shadow-md' : 'opacity-50 hover:opacity-100 hover:scale-110'}`}
                            onClick={() => {
                                setLanguage('pt');
                                document.documentElement.lang = 'pt';
                            }}
                            title="Português"
                        >
                            <svg width="28" height="20" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="block">
                                <rect width="20" height="15" fill="#009c3b" rx="2" />
                                <path d="M10 2L2 7.5L10 13L18 7.5L10 2Z" fill="#ffdf00" />
                                <circle cx="10" cy="7.5" r="2.5" fill="#002776" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`bg-transparent border-none p-0 cursor-pointer transition-all duration-200 ${i18n.language?.startsWith('en') ? 'opacity-100 scale-110 drop-shadow-md' : 'opacity-50 hover:opacity-100 hover:scale-110'}`}
                            onClick={() => {
                                setLanguage('en');
                                document.documentElement.lang = 'en';
                            }}
                            title="English"
                        >
                            <svg width="28" height="20" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="block">
                                <rect width="20" height="15" fill="#fff" rx="2" />
                                <rect width="5" height="15" fill="#FF0000" rx="0" />
                                <rect x="15" width="5" height="15" fill="#FF0000" rx="0" />
                                <path d="M10 3l-1 2h-1l1 1.5-1 1.5h1l1 2 1-2h1l-1-1.5 1-1.5h-1z" fill="#FF0000" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};



/* HERO SECTION (Immersive / Emotional) */
const Hero = () => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleJoinWaitlist = async (e) => {
        e.preventDefault();

        if (!email || !email.includes('@')) {
            setError(t('landing.waitlist.errorInvalid'));
            trackWaitlistError({ role: 'athlete', source: WAITLIST_SOURCE, reason: 'invalid_email' });
            return;
        }

        setLoading(true);
        setError('');

        try {
            const normalizedEmail = email.toLowerCase();
            const normalizedLang = (i18n.language || 'pt').toLowerCase().startsWith('en') ? 'en' : 'pt';
            const environment = getEnvironmentTag();

            await addDoc(collection(db, "waitlist"), {
                email: normalizedEmail,
                createdAt: serverTimestamp(),
                source: WAITLIST_SOURCE,
                status: 'pending',
                language: normalizedLang,
                role: 'athlete',
                environment
            });

            trackWaitlistSubmit({ role: 'athlete', source: WAITLIST_SOURCE, email: normalizedEmail, language: normalizedLang });
            setSuccess(true);
            setEmail('');
        } catch (err) {
            console.error("Erro ao salvar:", err);
            setError(t('landing.waitlist.errorGeneric'));
            trackWaitlistError({ role: 'athlete', source: WAITLIST_SOURCE, reason: 'firestore_write_failed' });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setSuccess(false);
        setEmail('');
        setError('');
    };

    return (
        <div className="relative min-h-screen flex flex-col items-center justify-start sm:items-center sm:justify-center overflow-hidden pb-40 sm:pb-0">
            {/* Immersive Background Image */}
            <div className="absolute inset-0 z-0">
                <img
                    src={heroRunnerImg}
                    alt="Runner on mountain trail"
                    className="w-full h-full object-cover object-center"
                />
                {/* Vertical fade - terminates early (top 30% only) for legibility */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-950/20 to-transparent"></div>
            </div>

            {/* Horizontal gradient overlay - constrained to content width for precise fade */}
            <div className="absolute inset-0 z-0 flex">
                <div className="max-w-3xl w-full bg-gradient-to-r from-slate-950/70 via-slate-950/40 to-transparent"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full flex items-center justify-start">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-950/80 border border-cyan-500/50 text-cyan-400 text-xs font-bold tracking-wider uppercase mb-8 sm:mb-10 backdrop-blur-md shadow-lg">
                        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                        {t('landing.hero.badge_v2')}
                    </div>

                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-display font-bold leading-tight mb-8 text-white drop-shadow-lg">
                        {t('landing.hero.titleMain_v2')} <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                            {t('landing.hero.titleAccent_v2')}
                        </span>
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-slate-200 mb-10 leading-relaxed font-sans max-w-2xl font-light drop-shadow-md">
                        {t('landing.hero.description_v2')}
                    </p>

                    {/* Waitlist Form Section */}
                    <div className="w-full sm:max-w-md relative z-20 origin-top scale-90 sm:scale-100">
                        {!success ? (
                            <form onSubmit={handleJoinWaitlist} noValidate className="flex flex-col gap-4">
                                <div className="relative group">
                                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                                    <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-xl p-4 transition-colors" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                if (error) setError('');
                                            }}
                                            placeholder={t('landing.waitlist.inputPlaceholder_v2')}
                                            required
                                            disabled={loading}
                                            className="w-full bg-transparent border-none px-4 py-3 focus:ring-0 text-white placeholder:text-slate-500 font-sans h-12 outline-none"
                                        />
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full px-8 py-3 bg-gradient-to-r from-cyan-500 to-emerald-600 text-white rounded-lg font-bold text-lg hover:shadow-lg hover:brightness-110 transition-all disabled:opacity-50 border-none"
                                        >
                                            {loading ? (
                                                <span className="flex items-center gap-2">
                                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                </span>
                                            ) : (
                                                t('landing.waitlist.button')
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div className="flex items-center gap-2 text-red-400 text-sm font-medium px-2 animate-fadeIn bg-red-900/20 py-2 rounded-lg border border-red-500/20 w-fit">
                                        <span className="material-symbols-outlined text-lg">error</span>
                                        {error}
                                    </div>
                                )}
                            </form>
                        ) : (
                            <div className="bg-emerald-950/80 border border-emerald-500/30 rounded-xl p-8 backdrop-blur-xl animate-fadeIn shadow-2xl">
                                <div className="flex items-center gap-3 text-emerald-400 font-bold text-xl mb-3">
                                    <span className="material-symbols-outlined text-3xl">check_circle</span>
                                    {t('landing.waitlist.successMessage')}
                                </div>
                                <p className="text-slate-300 text-base mb-6 leading-relaxed">{t('landing.waitlist.successMessageBody')}</p>
                                <button
                                    onClick={handleReset}
                                    className="px-4 py-2 rounded-lg bg-emerald-900/50 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-900 hover:text-white transition-all font-sans font-semibold text-sm"
                                >
                                    {t('landing.waitlist.resetButton')}
                                </button>
                            </div>
                        )}


                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-12 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] animate-bounce cursor-pointer z-10"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
                <span className="text-xs uppercase tracking-widest font-semibold">{t('landing.hero.scrollIndicator')}</span>
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </div>
        </div>
    );
};



const HowItWorks = () => {
    const { t } = useTranslation();

    const stepsData = [
        {
            icon: 'sync',
            title: t('landing.howItWorks.step1.title_v2'),
            description: t('landing.howItWorks.step1.desc_v2'),
            align: 'left'
        },
        {
            icon: 'calendar_today',
            title: t('landing.howItWorks.step2.title_v2'),
            description: t('landing.howItWorks.step2.desc_v2'),
            align: 'right'
        },
        {
            icon: 'trending_up',
            title: t('landing.howItWorks.step3.title_v2'),
            description: t('landing.howItWorks.step3.desc_v2'),
            align: 'left'
        }
    ];

    const handleStepClick = (currentIndex) => {
        if (currentIndex < stepsData.length - 1) {
            const nextElement = document.getElementById(`how-it-works-step-${currentIndex + 1}`);
            nextElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            const faqElement = document.getElementById('faq-section');
            faqElement?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section id="how-it-works" className="py-32 relative overflow-hidden bg-slate-950/30 scroll-mt-0">
            {/* Background Decorative Fx */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-3xl z-0 pointer-events-none"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-24">
                    <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-6 uppercase tracking-tight">
                        {t('landing.howItWorks.mainTitle_v2')}
                    </h2>
                    <p className="text-xl text-slate-400 font-sans max-w-2xl mx-auto font-light">
                        {t('landing.howItWorks.subtitle_v2')}
                    </p>
                </div>

                <div className="relative max-w-7xl mx-auto">
                    {/* Central Connecting "Laser" Line - Absolute Background */}
                    <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-500/20 via-emerald-500/50 to-cyan-500/20 transform md:-translate-x-1/2 rounded-full hidden md:block"></div>

                    {stepsData.map((step, idx) => (
                        <div
                            key={idx}
                            id={`how-it-works-step-${idx}`}
                            className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center mb-24 last:mb-0 relative"
                        >

                            {/* LEFT COLUMN - Visible only if align === 'left' */}
                            <div className="md:col-span-5 order-2 md:order-1 flex justify-end">
                                {step.align === 'left' && (
                                    <div
                                        onClick={() => handleStepClick(idx)}
                                        className="w-full p-6 md:p-8 rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/5 hover:border-cyan-500/30 transition-all duration-500 group relative overflow-hidden cursor-pointer hover:-translate-y-1 text-left"
                                    >
                                        {/* Hover Glow */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                        {/* Big Number Watermark (Right) */}
                                        <span className="absolute top-2 right-4 text-7xl font-display font-bold text-white/5 select-none z-0">
                                            0{idx + 1}
                                        </span>

                                        <h3 className="text-2xl font-bold text-white mb-3 font-display relative z-10 group-hover:text-cyan-400 transition-colors">
                                            {step.title}
                                        </h3>
                                        <p className="text-slate-400 font-sans leading-relaxed relative z-10 mt-6">
                                            {step.description}
                                        </p>

                                        {/* Next Hint (Botton Right) */}
                                        <div className="mt-4 flex items-center justify-end gap-2 text-xs font-bold uppercase tracking-widest text-cyan-500/0 group-hover:text-cyan-500 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                                            <span>{t('landing.howItWorks.next')}</span>
                                            <span className="material-symbols-outlined text-sm">arrow_downward</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* CENTER COLUMN (Icon) - Always Visible */}
                            <div className="md:col-span-2 flex justify-center order-1 md:order-2 relative z-20 mb-8 md:mb-0">
                                <div className="w-16 h-16 rounded-full bg-slate-950 border-2 border-cyan-500/50 shadow-[0_0_20px_rgba(34,211,238,0.3)] flex items-center justify-center relative group">
                                    <div className="absolute inset-0 rounded-full bg-cyan-400/20 blur-md animate-pulse"></div>
                                    <span className="material-symbols-outlined text-cyan-400 text-3xl relative z-10 group-hover:scale-110 transition-transform">
                                        {step.icon}
                                    </span>
                                </div>
                            </div>

                            {/* RIGHT COLUMN - Visible only if align === 'right' */}
                            <div className="md:col-span-5 order-2 md:order-3 flex justify-start">
                                {step.align === 'right' && (
                                    <div
                                        onClick={() => handleStepClick(idx)}
                                        className="w-full p-6 md:p-8 rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/5 hover:border-cyan-500/30 transition-all duration-500 group relative overflow-hidden cursor-pointer hover:-translate-y-1 text-right"
                                    >
                                        {/* Hover Glow */}
                                        <div className="absolute inset-0 bg-gradient-to-l from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                        {/* Big Number Watermark (Left) */}
                                        <span className="absolute top-2 left-4 text-7xl font-display font-bold text-white/5 select-none z-0">
                                            0{idx + 1}
                                        </span>

                                        <h3 className="text-2xl font-bold text-white mb-3 font-display relative z-10 group-hover:text-cyan-400 transition-colors">
                                            {step.title}
                                        </h3>
                                        <p className="text-slate-400 font-sans leading-relaxed relative z-10 mt-6">
                                            {step.description}
                                        </p>

                                        {/* Next Hint (Bottom Left) */}
                                        <div className="mt-4 flex items-center justify-start gap-2 text-xs font-bold uppercase tracking-widest text-cyan-500/0 group-hover:text-cyan-500 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                                            <span className="material-symbols-outlined text-sm">arrow_downward</span>
                                            <span>{t('landing.howItWorks.next')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Footer = () => {
    const { t, i18n } = useTranslation();

    return (
        <footer className="bg-background-dark border-t border-white/5 pt-16 pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
                    <div className="flex flex-col gap-6">
                        <Logo />
                        <p className="text-sm text-slate-400 leading-relaxed font-sans max-w-xs">
                            {t('landing.footer.description')}
                        </p>
                    </div>

                    <div>
                        <h4 className="font-display font-bold text-white mb-4">{t('landing.footer.sections.platform')}</h4>
                        <ul className="space-y-3 text-sm font-sans list-none p-0 m-0">
                            <li><a className="text-slate-400 no-underline hover:text-primary transition-colors" href="https://app.podiumrun.com/login">{t('landing.footer.links.loginAthlete')}</a></li>
                            <li><a className="text-slate-400 no-underline hover:text-primary transition-colors" href={i18n.language?.startsWith('en') ? '/coaches' : '/treinadores'}>{t('landing.footer.links.forCoaches')}</a></li>
                            <li><span className="text-slate-600 cursor-not-allowed">{t('landing.footer.links.pricing')}</span></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-display font-bold text-white mb-4">{t('landing.footer.sections.legal')}</h4>
                        <ul className="space-y-3 text-sm font-sans list-none p-0 m-0">
                            <li><a className="text-slate-400 no-underline hover:text-primary transition-colors" href={`https://podiumrun.com/terms?userLang=${i18n.language?.slice(0, 2)}`}>{t('landing.footer.links.terms')}</a></li>
                            <li><a className="text-slate-400 no-underline hover:text-primary transition-colors" href={`https://podiumrun.com/privacy?userLang=${i18n.language?.slice(0, 2)}`}>{t('landing.footer.links.privacy')}</a></li>
                            <li><a className="text-slate-400 no-underline hover:text-primary transition-colors" href={`https://podiumrun.com/support?userLang=${i18n.language?.slice(0, 2)}`}>{t('landing.footer.links.support')}</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-display font-bold text-white mb-4">{t('landing.footer.sections.connect')}</h4>
                        <ul className="space-y-3 text-sm font-sans list-none p-0 m-0">
                            <li>
                                <a className="text-slate-400 no-underline hover:text-primary transition-colors flex items-center gap-2" href={`mailto:${t('landing.footer.email')}`}>
                                    <span className="material-symbols-outlined text-sm">mail</span>
                                    {t('landing.footer.email')}
                                </a>
                            </li>
                            <li className="flex gap-4 pt-4">
                                <a className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:text-primary hover:bg-white/10 transition-all" href="#">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path></svg>
                                </a>
                                <a className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:text-primary hover:bg-white/10 transition-all" href="#">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path></svg>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="text-center text-[10px] uppercase tracking-widest text-slate-600 border-t border-white/5 pt-8 font-sans">
                    {t('landing.footer.copyright')}
                </div>
            </div>
        </footer>
    );
};


const LandingPageFAQ = () => {
    const { t } = useTranslation();

    const faqData = [
        {
            name: "Julia",
            age: `28 ${t('landing.faq.years')}`,
            avatar: athleteAvatar2,
            question: t('landing.faq.item1.question'),
            answer: t('landing.faq.item1.answer')
        },
        {
            name: "Carlos",
            age: `34 ${t('landing.faq.years')}`,
            avatar: athleteAvatar1,
            question: t('landing.faq.item2.question'),
            answer: t('landing.faq.item2.answer')
        },
        {
            name: "Roberto",
            age: `52 ${t('landing.faq.years')}`,
            avatar: athleteAvatar3,
            question: t('landing.faq.item3.question'),
            answer: t('landing.faq.item3.answer')
        }
    ];

    return (
        <section id="faq-section" className="py-24 relative bg-slate-50 scroll-mt-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-base text-cyan-600 font-bold tracking-widest uppercase font-display mb-2">
                        {t('landing.faq.badge')}
                    </h2>
                    <h3 className="text-3xl md:text-4xl font-display font-bold text-slate-900 mb-4">
                        {t('landing.faq.mainTitle')}
                    </h3>
                    <p className="text-slate-600 font-sans max-w-xl mx-auto">
                        {t('landing.faq.subtitle')}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {faqData.map((item, idx) => (
                        <div key={idx} className="flex flex-col gap-6">

                            {/* Question Bubble (Athlete) - LIGHT THEME */}
                            <div className="relative bg-white rounded-3xl rounded-tl-none p-6 border border-slate-200 shadow-sm">
                                <div className="flex items-center gap-4 mb-3">
                                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-100">
                                        <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div>
                                        <h4 className="text-slate-900 font-display font-bold leading-tight">{item.name}</h4>
                                        <span className="text-xs text-slate-500 font-sans">{item.age}</span>
                                    </div>
                                </div>
                                <p className="text-lg text-slate-700 font-display font-semibold italic">
                                    "{item.question}"
                                </p>
                                {/* Connector Triangle */}
                                <div className="absolute -left-2 top-0 w-4 h-4 bg-white skew-x-12 transform rotate-45 border-l border-b border-slate-200 disabled-triangle-hack"></div>
                            </div>

                            {/* Connector Line */}
                            <div className="h-8 w-0.5 bg-gradient-to-b from-slate-300 to-cyan-500/50 mx-auto"></div>

                            {/* Answer Bubble (Mariana) - LIGHT THEME (Cyber-Light) */}
                            <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl rounded-br-none p-6 shadow-xl shadow-cyan-900/10 border border-slate-700">
                                <div className="flex items-center gap-4 mb-4 flex-row-reverse justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-cyan-400 font-bold tracking-wide">Mariana</span>
                                        <div className="w-10 h-10 rounded-full overflow-hidden border border-cyan-500/50 bg-slate-950">
                                            <img src={marianaAvatar} alt="Mariana AI" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                </div>
                                <p className="text-slate-300 font-sans text-sm leading-relaxed">
                                    {item.answer}
                                </p>
                            </div>

                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ==========================================
   MAIN COMPONENT
   ========================================== */
const LandingPageV2 = () => {
    const { i18n } = useTranslation();
    const isEn = i18n.language?.startsWith('en');

    return (
        <div id="landing-page-root" className="h-screen w-full font-sans bg-slate-950 overflow-y-auto scroll-smooth">
            <PageMeta
                role="athlete"
                lang={isEn ? 'en' : 'pt'}
                path={isEn ? '/runners' : '/'}
                image="/hero-runner-og.jpg"
                title={isEn
                    ? "Podium | Don't Just Run. Evolve."
                    : 'Podium | Não Apenas Corra. Evolua.'}
                description={isEn
                    ? 'Forget static PDF spreadsheets. Podium uses your real Strava data to build living training plans that adapt to your routine.'
                    : 'Esqueça as planilhas estáticas em PDF. A Podium usa seus dados reais do Strava para criar planos vivos que se adaptam à sua rotina.'}
            />
            <Navbar />
            <main>
                <Hero />
                <Features />
                <HowItWorks />
                <LandingPageFAQ />
            </main>
            <Footer />
        </div>
    );
};

export default LandingPageV2;
